import { ArrayFieldClient, ClientField, ClientTab, Field, FieldWithSubFields, FieldWithSubFieldsClient, TabsField, TabsFieldClient } from 'payload'
import { fieldHasSubFields } from 'payload/shared'
import { trim } from '@utils/misc'
import { shouldBeArray } from '@utils/objects';

export type FlattenCollectionField = CustomPayloadField<Exclude<Field, FieldWithSubFields | TabsField>>;
export type FlattenCollectionFieldClient = CustomPayloadClientField<Exclude<ClientField, FieldWithSubFieldsClient | TabsFieldClient>>;

export type FlattenCollectionFieldClientWithPath = (FlattenCollectionFieldClient & { fullpath: string });
export type FlattenCollectionFieldClientWithPathWithArray = ((FlattenCollectionFieldClient | ArrayFieldClient) & { fullpath: string });

export type ClientNamedTab = ClientTab & { name?: string };

/**
 * Get text label from a Field.
 * In general, we just use StaticLabels, so it's pretty safe to return strings everywhere.
 *
 * @param collection
 * @returns
 */
export function getFieldLabel(field: CustomPayloadField<Field> | CustomPayloadClientField<ClientField>): string {
  if ('label' in field && typeof field?.label === 'string') {
    return field.label;
  }

  if ('name' in field) {
    return field.name;
  }

  return '';
}

/**
 * Map a array of collection fields considering FieldWithSubFields
 *
 * @param fields
 * @param callback
 * @returns
 */
export function mapCollectionFields(fields: CustomPayloadField<Field>[], callback: (field: FlattenCollectionField) => CustomPayloadField<Field> | null): CustomPayloadField<Field>[] {
  const newFields: typeof fields = [];

  for (let i = 0; i < fields.length; i++) {
    const field: any = fields[i];

    // For Tabs
    if (field?.tabs) {
      for (let j = 0; j < field?.tabs.length; j++) {
        field.tabs[j].fields = mapCollectionFields(field.tabs[j].fields || [], callback);
      }

      newFields.push(field);
      continue;
    }

    // For ArrayField | CollapsibleField | GroupField | RowField (FieldWithSubFields)
    if (fieldHasSubFields(field)) {
      field.fields = mapCollectionFields(field.fields || [], callback);

      if (! field?.name) {
        newFields.push(field);
        continue;
      }
    }

    // For fields
    const mapped = callback(field);
    if (mapped === null) {
      continue;
    }

    newFields.push(mapped);
  }

  return newFields;
}

export function mapCollectionFieldsClient(fields: CustomPayloadClientField<ClientField>[], callback: (field: CustomPayloadClientField<FlattenCollectionFieldClient>) => CustomPayloadClientField<ClientField>): CustomPayloadClientField<ClientField>[] {
  return mapCollectionFields(fields as any, callback as any) as any;
}

/**
 * Filter a array of collection fields considering FieldWithSubFields
 *
 * @param fields
 * @param names
 * @returns
 */
export function filterCollectionFields(fields: CustomPayloadField<Field>[], names: string | string[]): CustomPayloadField<Field>[] {
  const namesList = shouldBeArray(names);
  const filtered: typeof fields = [];

  for (let i = 0; i < fields.length; i++) {
    const field: any = fields[i];
    const fieldName = field?.name ?? '';

    if (fieldName && namesList.includes(field.name)) {
      continue;
    }

    // For Tabs
    if (field?.tabs) {
      for (let j = 0; j < field?.tabs.length; j++) {
        field.tabs[j].fields = filterCollectionFields(field.tabs[j].fields || [], names);
      }

      filtered.push(field);
      continue;
    }

    // For ArrayField | CollapsibleField | GroupField | RowField (FieldWithSubFields)
    if (fieldHasSubFields(field)) {
      field.fields = filterCollectionFields(field.fields || [], names);
    }

    filtered.push(field);
  }

  return filtered;
}

/**
 * Flatten fields from rows
 *
 * @param fields
 * @returns
 */
export function removeRowFields(fields: CustomPayloadField<Field>[]): CustomPayloadField<Field>[] {
  const flattened: typeof fields = [];

  for (let i = 0; i < fields.length; i++) {
    const field: any = fields[i];
    const fieldType = field?.type ?? '';

    // For Rows
    if (fieldType === 'row') {
      for (let j = 0; j < (field?.fields ?? []).length; j++) {
        flattened.push(field?.fields[j]);
      }

      continue;
    }

    flattened.push(field);
  }

  return flattened;
}

/**
 * Map a array of collection fields considering FieldWithSubFields
 *
 * @param fields
 * @param callback
 * @returns
 */
export function removeEmptyParentFields(fields: CustomPayloadField<Field>[]): CustomPayloadField<Field>[] {
  const newFields: CustomPayloadField<Field>[] = [];

  for (let i = 0; i < fields.length; i++) {
    const field: any = fields[i];

    // For Tabs
    if ('tabs' in field) {
      const tabs: any[] = [];
      for (let j = 0; j < field.tabs.length; j++) {
        field.tabs[j].fields = removeEmptyParentFields(field.tabs[j].fields ?? []);

        if (field.tabs[j].fields.length) {
          tabs.push(field.tabs[j]);
        }
      }

      if (tabs.length) {
        field.tabs = tabs;
        newFields.push(field);
      }

      continue;
    }

    // For ArrayField | CollapsibleField | GroupField | RowField (FieldWithSubFields)
    if (fieldHasSubFields(field)) {
      field.fields = removeEmptyParentFields(field.fields ?? []);

      if (field.fields.length) {
        newFields.push(field);
      }

      continue;
    }

    // Fields
    newFields.push(field);
  }

  return newFields;
}

export function removeEmptyParentFieldsClient(fields: CustomPayloadClientField<ClientField>[]): CustomPayloadClientField<ClientField>[] {
  return removeEmptyParentFields(fields as any) as any;
}

/**
 * Sort object keys
 *
 * @param {Array} array
 * @returns {Array}
 */
export function sortCollectionFieldClient<T extends ClientField>(fields: CustomPayloadClientField<T>[], onStart: string[] = ['id', 'title'], onEnd: string[] = ['createdAt', 'updatedAt']): CustomPayloadClientField<T>[] {
  const shouldBeOnStart = (name?: string): boolean => onStart.includes(name ?? '');
  const shouldBeOnEnd = (name?: string): boolean => onEnd.includes(name ?? '');

  const startFields: CustomPayloadClientField<T>[] = [];
  const middleFields: CustomPayloadClientField<T>[] = [];
  const endFields: CustomPayloadClientField<T>[] = [];

  for (const field of fields) {
    const name = 'name' in field ? field.name : undefined;

    if (shouldBeOnStart(name)) {
      startFields.push(field);
      continue;
    }

    if (shouldBeOnEnd(name)) {
      endFields.push(field);
      continue;
    }

    middleFields.push(field);
  }

  return [...startFields, ...middleFields, ...endFields];
}

/**
 * Flatten fields to array of fields.
 * Adds a "fullpath" property with "field.subfields" values.
 *
 * @param {Array} array
 * @returns {Object|null}
 */
export function flattenCollectionFieldClient(fields: CustomPayloadClientField<ClientField>[], flattenArray?: true, parent?: string): FlattenCollectionFieldClientWithPath[];
export function flattenCollectionFieldClient(fields: CustomPayloadClientField<ClientField>[], flattenArray: false, parent?: string): FlattenCollectionFieldClientWithPathWithArray[];

export function flattenCollectionFieldClient(fields: CustomPayloadClientField<ClientField>[], flattenArray: boolean = true, parent: string = ''): FlattenCollectionFieldClientWithPath[] | FlattenCollectionFieldClientWithPathWithArray[] {
  type FlattenResult<T extends boolean> = T extends true ? FlattenCollectionFieldClientWithPath : FlattenCollectionFieldClientWithPathWithArray;

  const _flattenCollectionFieldClient = <T extends boolean>(fields: CustomPayloadClientField<ClientField>[], flattenArray: T, parent: string): FlattenResult<T>[] => {
    const flatten: FlattenResult<T>[] = [];

    for (let i = 0; i < fields.length; i++) {
      const field = fields[i];
      const fullpath = trim(`${parent}.${('name' in field ? field.name : '')}`, ' .');

      if ('tabs' in field) {
        const tabs = (field as TabsFieldClient).tabs;

        for (let j = 0; j < tabs.length; j++) {
          const tab = tabs[j] as ClientNamedTab;
          const tabsFields = _flattenCollectionFieldClient(tab.fields, flattenArray, fullpath);
          flatten.push(...tabsFields);
        }

        continue;
      }

      if (fieldHasSubFields(field) && (flattenArray || field.type !== 'array')) {
        const subFields = _flattenCollectionFieldClient(field.fields, flattenArray, fullpath);
        flatten.push(...subFields);
        continue;
      }

      const fieldWithPath = { ...(field as FlattenCollectionFieldClient | ArrayFieldClient), fullpath } as FlattenResult<T>;
      flatten.push(fieldWithPath);
    }

    return flatten as any;
  };

  return _flattenCollectionFieldClient(fields, flattenArray, parent);
}
